import React, { useEffect, useState } from 'react';
import { admin } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Sparkles, Trash2, Eye, EyeOff, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const STATUS_STYLE = {
  live: 'bg-green-500/10 text-green-300 border-green-500/30',
  hidden: 'bg-gray-500/10 text-gray-300 border-gray-500/30',
  pending_review: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30',
  draft: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
};

export default function AdminSliderFormsPanel() {
  const [forms, setForms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState('forms');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        admin.listSliderForms(),
        admin.listSliderSubmissions('pending_review'),
      ]);
      setForms(f || []); setSubs(s || []);
    } catch (err) { toast({ title: 'Load failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleHide = async (f) => {
    setBusy(`${f.id}:toggle`);
    try {
      await admin.updateSliderForm(f.id, { hidden: f.status !== 'hidden' });
      await load();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete the slider form for ${f.businessName || f.businessId}? It will auto-regenerate on next consumer click.`)) return;
    setBusy(`${f.id}:del`);
    try { await admin.deleteSliderForm(f.id); await load(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const moderateForm = async (f, decision) => {
    const note = decision === 'reject' ? (window.prompt('Rejection note (optional):') || '') : '';
    setBusy(`${f.id}:mod`);
    try { await admin.moderateSliderForm(f.id, decision, note); await load(); }
    catch (err) { toast({ title: 'Action failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const moderateSub = async (s, decision) => {
    const note = decision === 'reject' ? (window.prompt('Rejection note (optional):') || '') : '';
    setBusy(`${s.id}:modsub`);
    try { await admin.moderateSliderSubmission(s.id, decision, note); await load(); }
    catch (err) { toast({ title: 'Action failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const pendingForms = forms.filter(f => f.status === 'pending_review' || f.pendingEdits);

  return (
    <div className="space-y-4" data-testid="admin-slider-forms-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-bold text-lg flex items-center gap-2"><Sparkles size={16}/> Slider Signup Forms</h2>
        <div className="inline-flex rounded-full border border-neutral-800 overflow-hidden text-xs">
          <button onClick={() => setTab('forms')} data-testid="tab-forms"
            className={`px-3 py-1.5 ${tab === 'forms' ? 'bg-green-600 text-black font-semibold' : 'text-gray-400'}`}>
            Forms ({forms.length})
          </button>
          <button onClick={() => setTab('pending')} data-testid="tab-pending"
            className={`px-3 py-1.5 ${tab === 'pending' ? 'bg-yellow-600 text-black font-semibold' : 'text-gray-400'}`}>
            Pending edits ({pendingForms.length})
          </button>
          <button onClick={() => setTab('subs')} data-testid="tab-subs"
            className={`px-3 py-1.5 ${tab === 'subs' ? 'bg-red-600 text-black font-semibold' : 'text-gray-400'}`}>
            Flagged subs ({subs.length})
          </button>
        </div>
      </div>

      {loading ? <div className="p-8 text-center text-gray-500"><Loader2 className="animate-spin mx-auto" size={20}/></div> : (
        <>
          {tab === 'forms' && (
            <div className="bg-[#0f0f0f] border border-[#1f1f1f] rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="text-gray-500 text-left bg-black/40">
                  <tr><th className="px-3 py-2">Business</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Questions</th><th className="px-3 py-2">Updated</th><th className="px-3 py-2 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {forms.map(f => (
                    <tr key={f.id} className="border-t border-[#1f1f1f]" data-testid={`admin-slider-form-row-${f.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-white">{f.businessName || f.businessId}</div>
                        <div className="text-[10px] text-gray-500">{f.businessCategory}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_STYLE[f.status] || ''}`}>{f.status}</span>
                      </td>
                      <td className="px-3 py-2">{(f.questions || []).length}</td>
                      <td className="px-3 py-2 text-gray-500">{f.updatedAt ? new Date(f.updatedAt).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 text-right space-x-2">
                        <button onClick={() => toggleHide(f)} disabled={busy === `${f.id}:toggle`}
                          data-testid={`admin-slider-form-toggle-${f.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-neutral-700 hover:bg-neutral-800">
                          {f.status === 'hidden' ? <><Eye size={11}/> Show</> : <><EyeOff size={11}/> Hide</>}
                        </button>
                        <button onClick={() => del(f)} disabled={busy === `${f.id}:del`}
                          data-testid={`admin-slider-form-delete-${f.id}`}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10">
                          <Trash2 size={11}/> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {forms.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">No slider forms yet.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'pending' && (
            <div className="space-y-3">
              {pendingForms.length === 0 && <div className="p-8 text-center text-gray-500 text-sm">No pending edits.</div>}
              {pendingForms.map(f => (
                <div key={f.id} className="bg-[#0f0f0f] border border-yellow-500/30 rounded-xl p-4" data-testid={`admin-slider-pending-${f.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold">{f.businessName}</div>
                      <div className="text-xs text-yellow-300 flex items-center gap-1 mt-1"><AlertTriangle size={12}/> {f.pendingEdits?.flagReason || 'Review pending'}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => moderateForm(f, 'approve')} disabled={busy === `${f.id}:mod`}
                        data-testid={`admin-slider-approve-${f.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-500 text-black font-semibold">
                        <CheckCircle2 size={11}/> Approve
                      </button>
                      <button onClick={() => moderateForm(f, 'reject')} disabled={busy === `${f.id}:mod`}
                        data-testid={`admin-slider-reject-${f.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-red-500/30 text-red-300">
                        <XCircle size={11}/> Reject
                      </button>
                    </div>
                  </div>
                  {f.pendingEdits?.questions && (
                    <ul className="text-xs text-gray-300 mt-3 list-disc pl-5">
                      {f.pendingEdits.questions.map((q, i) => <li key={i}><b>[{q.type}]</b> {q.prompt}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === 'subs' && (
            <div className="space-y-3">
              {subs.length === 0 && <div className="p-8 text-center text-gray-500 text-sm">No flagged submissions.</div>}
              {subs.map(s => (
                <div key={s.id} className="bg-[#0f0f0f] border border-red-500/30 rounded-xl p-4" data-testid={`admin-slider-sub-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold">{s.businessName}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{s.contact?.name} · {s.contact?.email}</div>
                      <div className="text-xs text-red-300 flex items-center gap-1 mt-1"><AlertTriangle size={12}/> {s.flagReason}</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => moderateSub(s, 'approve')} disabled={busy === `${s.id}:modsub`}
                        data-testid={`admin-slider-sub-approve-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-500 text-black font-semibold">
                        <CheckCircle2 size={11}/> Approve + send email
                      </button>
                      <button onClick={() => moderateSub(s, 'reject')} disabled={busy === `${s.id}:modsub`}
                        data-testid={`admin-slider-sub-reject-${s.id}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-red-500/30 text-red-300">
                        <XCircle size={11}/> Reject
                      </button>
                    </div>
                  </div>
                  {s.answers?.length > 0 && (
                    <ul className="text-xs text-gray-300 mt-3 list-disc pl-5">
                      {s.answers.map((a, i) => <li key={i}><b>{a.questionId}:</b> {Array.isArray(a.value) ? a.value.join(', ') : String(a.value)}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
